import { useBlocker } from 'react-router';
import { useSuspenseMaxEndpointOIDCUsersQuery } from '#api/endpoint-oidc-users.queries.ts';
import { ButtonGroup, ButtonGroupItem } from '#components/patterns/button-group/ButtonGroup.tsx';
import { TableLayout } from '#components/patterns/layouts/table/TableLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useMatchMedia } from '#hooks/useMatchMedia.ts';
import { TooManyOidcProvidersModal } from '#screens/settings/sso/add/TooManyOidcProvidersModal.tsx';
import { GoogleDirectorySyncButton } from '#screens/settings/sso/google-idp/GoogleDirectorySyncButton.tsx';
import { useGoogleDirectorySyncAvailable } from '#screens/settings/sso/google-idp/useGoogleDirectorySyncAvailable.ts';
import { useSuspenseAllOIDCProvidersQuery } from '#screens/settings/sso/sso-page/SSOSettingsPage.queries.ts';
import { useSuspenseIsProvidersLimitReached } from '#screens/settings/sso/util/useSuspenseIsProvidersLimitReached.ts';
import { InviteUsersButton } from '../downloads/InviteUsersButton.tsx';
import { EmptyEndpointUsers } from '../empty/EmptyEndpointUsers.tsx';
import { EndpointOIDCUsersList } from '../list/EndpointOIDCUsersList.tsx';
import { EndpointOIDCUsersSearch } from '../list/EndpointOIDCUsersSearch.tsx';
import { usePrefetchHostCountsByUser } from '../list/EndpointOIDCUsersTable.queries.ts';
import { filterUsers } from '../list/filterUsers.ts';
import { useUsersSearch } from '../list/useUsersSearch.ts';

/** Narrowest viewport that fits the header in one row: title (83px), the search's 150px floor, the Invite users
 * button (123px), two 16px gaps, and 32px body padding = 420px, plus 2px allowance. Re-measure if a control's width
 * changes. */
const STACK_WIDTH = '422px';
/** {@link STACK_WIDTH} plus the Sync now button (110px, about 0.9 of Invite users) and its 16px gap. */
const STACK_WIDTH_WITH_SYNC = '548px';

export function EndpointUsersPage() {
  // Ahead of the suspending queries, so the table's host counts load alongside them instead of after.
  usePrefetchHostCountsByUser();
  const { isProvidersLimitReached, endpointProvidersLimit } = useSuspenseIsProvidersLimitReached();
  const blocker = useBlocker(
    ({ nextLocation }) => isProvidersLimitReached && nextLocation.pathname === '/settings/sso/add'
  );
  const users = useSuspenseMaxEndpointOIDCUsersQuery().data.data;
  const hasEnabledProvider = useSuspenseAllOIDCProvidersQuery().data.endpoint.data.some((provider) => provider.enabled);
  const syncAvailable = useGoogleDirectorySyncAvailable();
  const [search, setSearch] = useUsersSearch();
  const isStacked = useMatchMedia(`(max-width: ${syncAvailable ? STACK_WIDTH_WITH_SYNC : STACK_WIDTH})`);

  const tooManyProvidersModal =
    blocker.state === 'blocked' && isProvidersLimitReached ?
      <TooManyOidcProvidersModal onClose={blocker.reset} providersLimit={endpointProvidersLimit} />
    : null;

  if (users.length === 0) {
    return (
      <>
        <EmptyEndpointUsers />
        {tooManyProvidersModal}
      </>
    );
  }

  return (
    <TableLayout
      isEmpty={filterUsers(users, search).length === 0}
      header={
        <PageHeader title="Users" stacked={isStacked} preventTitleWrap={true}>
          <EndpointOIDCUsersSearch value={search} onChange={setSearch} fullWidth={isStacked} />
          {/* Without an enabled provider there's nothing to sign in to */}
          {syncAvailable ?
            <ButtonGroup className="shrink-0">
              <ButtonGroupItem>
                <GoogleDirectorySyncButton size="small" />
              </ButtonGroupItem>
            </ButtonGroup>
          : null}
          {hasEnabledProvider ?
            <ButtonGroup className="shrink-0">
              <ButtonGroupItem>
                <InviteUsersButton />
              </ButtonGroupItem>
            </ButtonGroup>
          : null}
        </PageHeader>
      }
    >
      <EndpointOIDCUsersList users={users} />
      {tooManyProvidersModal}
    </TableLayout>
  );
}
